import useNetworkRequest from "./useNetworkRequest";
import { InputEscapeResult } from "../Types/NetworkRequest";
import {
  escapeRoomApiHost,
  resultsEndpoint,
  validateRoomCodeEndpoint,
  revealRoomCodeEndpoint,
  teamManagementEndpoint,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  defaultProfileName,
} from "../Config/config";
import { ValidateRoomCodeResponse, CheckNameExistsResponse, RevealRoomCodeResponse } from "../Types/NetworkRequest";
import { ResponseEscapeResult } from "../Types/api";
import { DifficultyRatings, Event, ProfileSettings } from "../Types/Game";
import { useAuth } from "react-oidc-context";


const useEscapeRoomApi = () => {
  const auth = useAuth();
  const { makeGetRequest, makePostRequest } = useNetworkRequest(auth);

type BrandingKey = "gemba" | "actica" | "ntegra";

const brandingKeyForEvent = (eventId: string) => `branding:${eventId}`;

const readBrandingForEvent = (eventId?: string | null): BrandingKey | undefined => {
  if (!eventId) return undefined;
  const raw = localStorage.getItem(brandingKeyForEvent(eventId));
  return raw === "actica" || raw === "ntegra" || raw === "gemba" ? raw : undefined;
};

const writeBrandingForEvent = (eventId?: string | null, branding?: string) => {
  if (!eventId) return;
  if (branding === "actica" || branding === "ntegra" || branding === "gemba") {
    localStorage.setItem(brandingKeyForEvent(eventId), branding);
  }
};


  const retrieveLeaderboardResults = async (): Promise<ResponseEscapeResult[]> => {
    try {
      const response = await makeGetRequest<ResponseEscapeResult[]>({ endpoint: `${escapeRoomApiHost}/team/results/leaderboard` });
      return response;
    } catch (error) {
      throw error;
    }
  }

    const retrieveLeaderboardResultsByEvent = async (eventName: string): Promise<ResponseEscapeResult[]> => {
    try {
      const response = await makeGetRequest<ResponseEscapeResult[]>({ endpoint: `${escapeRoomApiHost}/team/results/leaderboard?event=${eventName}` });
      return response;
    } catch (error) {
      throw error;
    }
  }

  //NOT TESTED
  const deleteLeaderboardScore = async (event: string, name: string, room: string) => {
    try {
      const result = await makePostRequest({
        endpoint: `${escapeRoomApiHost}/${resultsEndpoint}/remove?event=${event}&name=${name}&room=${room}`
      });
      return result;

    } catch (error) {
      throw error;
    }
  };

  //attempt to add team and handle issues after
  const checkNameExists = async (
    name: string,
    schoolName: string,
    eventName: string
  ): Promise<CheckNameExistsResponse | undefined> => {
    try {
      const result = await makeGetRequest<CheckNameExistsResponse>({
        endpoint: `${escapeRoomApiHost}/${teamManagementEndpoint}?name=${name}${schoolName ? `&school=${schoolName}` : ""}&event=${eventName}`,
      });
      return result;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

const saveProfileSettings = async (body: unknown, eventName?: string) => {
  try {
    const result = await makePostRequest({
      endpoint: `${escapeRoomApiHost}/profile/add${eventName ? "?event=" + encodeURIComponent(eventName) : ""}`,
      body: JSON.stringify(body),
    });

    // transparently persist branding locally if provided in body
    try {
      const parsed = typeof body === "string" ? JSON.parse(body) : body;
      writeBrandingForEvent(eventName, parsed?.branding);
    } catch {}

    return result;
  } catch (error) {
    throw error;
  }
};

    const attachProfile = async (event: string, profile: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await makePostRequest({
        endpoint: `${escapeRoomApiHost}/profile/attach?event=${encodeURIComponent(event)}&profile=${encodeURIComponent(profile)}`
      });
    } catch (error) {
      //this doesn't work on passing it up 
      throw error;

    };
  }


const getProfileSettings = async (eventName: string): Promise<ProfileSettings> => {
  try {
    const result = await makeGetRequest<ProfileSettings & { branding?: BrandingKey }>({
      endpoint: `${escapeRoomApiHost}/profile/retrieve?event=${encodeURIComponent(eventName)}`
    });

    // If API didn’t return branding, pull from localStorage
    const apiBrand = result?.branding;
    if (!apiBrand) {
      const lsBrand = readBrandingForEvent(eventName);
      if (lsBrand) {
        return { ...result, branding: lsBrand };
      }
    }
    return result;

  } catch (error) {
    throw error;
  }
};

  const getAllProfiles = async (): Promise<ProfileSettings[]> => {
    try {

      const result = await makeGetRequest<ProfileSettings[]>({
        endpoint: `${escapeRoomApiHost}/profile/retrieve`
      });

      return result;
    } catch (error) {
      console.error(error);
      return [];

    };

  }

  const getAllEvents = async () : Promise<Event[]> => { 
    try {

      const result = await makeGetRequest<Event[]>({
        endpoint: `${escapeRoomApiHost}/profile/retrieve/events`
      });

      return result;
    } catch (error) {
      console.error(error);
      return [];

    };

  }

  const deleteEvent = async (eventId: string) => {
  try {
    const result = await makePostRequest({
      endpoint: `${escapeRoomApiHost}/profile/remove?event=${encodeURIComponent(eventId)}`,
    });
    return result;
  } catch (error) {
    throw error;
  }
};

  const setEscapeResult = async (body: InputEscapeResult) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await makePostRequest({
        endpoint: `${escapeRoomApiHost}/${resultsEndpoint}/add`,
        body: JSON.stringify(body),
      });
      // console.log(result);
    } catch (error) {
      console.error(error);
    }
  };

  const validateRoomCode = async (
    room: string,
    code: string,
    variant: number
  ): Promise<ValidateRoomCodeResponse> => {
    try {
      const result = await makeGetRequest<ValidateRoomCodeResponse>({
        endpoint: `${escapeRoomApiHost}/${validateRoomCodeEndpoint}?profile=changeme&code=${code}&variant=${variant}&room=${room}`,
      });
      if (result) {
        return {
          valid: result.valid,
          message: result.valid ? "Room code is valid!" : "Invalid room code.",
        };
      }
      return { valid: false, message: "No response from server." };
    } catch (error) {
      console.error("Error validating room code:", error);
      return {
        valid: false,
        message: "An error occurred while validating the room code.",
      };
    }
  };

  // Rooms 7 & 8 reveal their real door code to the player as part of their
  // own puzzle (a monitor video, a biometric scan, DorkyAI's readout) — this
  // fetches that code at reveal-time instead of it living in the UI's own
  // config, so it never ships in the JS bundle (ERD-53).
  const revealRoomCode = async (
    room: string,
    variant: number
  ): Promise<string | null> => {
    try {
      const result = await makeGetRequest<RevealRoomCodeResponse>({
        endpoint: `${escapeRoomApiHost}/${revealRoomCodeEndpoint}?room=${room}&variant=${variant}`,
      });
      return result?.code ?? null;
    } catch (error) {
      console.error("Error revealing room code:", error);
      return null;
    }
  };

  // Global room difficulty ratings (admin overrides shared by every event).
  // Returns the raw `ratings` map — callers sanitise it.
  const getDifficultyRatings = async (): Promise<unknown> => {
    const result = await makeGetRequest<{ ratings?: unknown }>({
      endpoint: `${escapeRoomApiHost}/profile/difficulty/retrieve`,
    });
    return result?.ratings;
  };

  const saveDifficultyRatings = async (ratings: DifficultyRatings) => {
    await makePostRequest({
      endpoint: `${escapeRoomApiHost}/profile/difficulty/save`,
      body: JSON.stringify({ ratings }),
    });
  };

  return {
    checkNameExists,
    setEscapeResult,
    validateRoomCode,
    revealRoomCode,
    saveProfileSettings,
    getProfileSettings,
    getAllProfiles,
    deleteLeaderboardScore,
    retrieveLeaderboardResults,
    retrieveLeaderboardResultsByEvent,
    attachProfile,
    getAllEvents,
    deleteEvent,
    getDifficultyRatings,
    saveDifficultyRatings
  };
};

export default useEscapeRoomApi;
